import React, { CSSProperties, FC, memo, useCallback, useState } from "react";
import Select, { MenuPlacement } from "react-select";
import { Controller } from "react-hook-form/dist/index.ie11";

import { mergeReactSelectSelectedFirst } from "~/shared/lib/selectedFirstOptions";

import { ISelectOption } from "../types";

import s from "./styles.less";
import clsx from "clsx";

interface Props {
  label: string;
  control: any;
  name: string;
  menuPlacement?: MenuPlacement;
  isDisabled?: boolean;
  options: ISelectOption[];
  loading?: boolean;
  isNameScroll?: boolean;
  isClearable?: boolean;
  isNonSrcoll?: boolean;
  onScrollBottom?: () => void;
  getSearchText?: (e) => void;
  customInput?: CSSProperties;
  customLabelClass?: string;
  error?: string;
  menuZIndex?: number;
  initialHeight?: number;
  showCounter?: boolean;
  maxElements?: number;
  valueDecorator?: (selected: ISelectOption[]) => ISelectOption[];
  customMenuStyle?: CSSProperties;
  customValueContainerStyle?: CSSProperties;
  setIsAllSelected?;
  newStyle?: any;
  placeholder?: string;
}

const MultiInput: FC<Props> = ({
  label,
  name,
  control,
  isDisabled,
  options,
  loading,
  isNameScroll,
  isClearable,
  isNonSrcoll,
  onScrollBottom,
  menuPlacement = "bottom",
  getSearchText,
  customInput,
  customLabelClass,
  error,
  menuZIndex = 2,
  initialHeight = 85,
  maxElements,
  valueDecorator,
  customMenuStyle = {},
  customValueContainerStyle = {},
  showCounter = false,
  setIsAllSelected,
  newStyle = null,
  placeholder,
}) => {
  const handleChange = useCallback((selected = [], onChange) => {
    selected = Array.isArray(selected) ? selected : [];
    if (valueDecorator) {
      onChange(valueDecorator(selected));
    } else {
      onChange(selected);
    }
  }, []);
  const [isFocus, setIsFocus] = useState(false);

  const onFocus = () => {
    setIsFocus(true);
  };
  const onBlur = () => {
    getSearchText && getSearchText("");
    setIsFocus(false);
  };

  const customClassNames = {
    menu: (provided, state) => ({
      ...provided,
      ...customMenuStyle,
      zIndex: menuZIndex,
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isFocused ? "white" : isDisabled ? "#dadee0" : "#65727c",
      fontSize: "11px",
    }),
    input: (provided, state) => ({
      ...provided,
      height: `${initialHeight}px`,
      ...customInput,
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      fontSize: "11px",
      whiteSpace: "normal",
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      alignItems: "flex-start",
      padding: "4px",
      width: "100%",
      overflowY: isNameScroll ? "scroll" : "scroll",
      maxHeight: isNameScroll ? "150px" : "100px",
      ...customValueContainerStyle,
    }),
    indicatorSeparator: (provided, state) => ({
      ...provided,
      display: "none",
    }),
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = "opacity 300ms";

      return { ...provided, opacity, transition };
    },
  };

  const customTheme = (theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary25: "#f17830",
      primary: "#ff9e1b",
      danger: "#a64921",
      dangerLight: "#f5dcba",
    },
  });

  return (
    <>
      <Controller
        control={control}
        name={name}
        render={({ value = [], onChange }) => {
          const sortedOptions = mergeReactSelectSelectedFirst(options, value);
          return (
            <>
              <div className={s.select__wrap}>
                {!value?.length && (
                  <span
                    className={clsx(s.select__label, customLabelClass, {
                      [s.select__label_focused]: isFocus,
                    })}
                  >
                    {label}
                  </span>
                )}
                {!isNonSrcoll ? (
                  <Select
                    theme={customTheme}
                    value={value}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    onChange={(selected) => {
                      getSearchText && getSearchText("");
                      handleChange(selected, onChange);

                      if (!selected || selected.length === 0) {
                        setIsAllSelected && setIsAllSelected(false);
                      }
                    }}
                    name={name}
                    isMulti={true}
                    options={sortedOptions}
                    isSearchable
                    isClearable={isClearable}
                    placeholder={placeholder ?? <></>}
                    isLoading={loading}
                    isDisabled={isDisabled}
                    openMenuOnClick={!(value.length === maxElements)}
                    {...(value.length === maxElements
                      ? { menuIsOpen: false }
                      : {})}
                    backspaceRemovesValue={false}
                    menuPlacement={menuPlacement}
                    styles={newStyle ?? customClassNames}
                    onMenuScrollToBottom={onScrollBottom}
                    onInputChange={(e) => {
                      e && getSearchText && getSearchText(e);
                      // e && addSChanged();
                    }}
                  />
                ) : (
                  <Select
                    theme={customTheme}
                    value={value}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onChange={(selected) => {
                      getSearchText && getSearchText("");
                      handleChange(selected, onChange);
                      // selected ? addSChanged() : removeSChanged();
                    }}
                    name={name}
                    isMulti={true}
                    options={sortedOptions}
                    isSearchable
                    isClearable={isClearable}
                    placeholder={<></>}
                    isLoading={loading}
                    isDisabled={isDisabled || value.length === maxElements}
                    menuPlacement={menuPlacement}
                    styles={customClassNames}
                  />
                )}
                {/*//todo add error boundary*/}
              </div>
              {!!error && <p className={s.error_text}>{error}</p>}
            </>
          );
        }}
      />
    </>
  );
};

export default memo(MultiInput);
